import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Printer, FileText, Sparkles, Download, CheckCircle2, RefreshCw, BookmarkPlus, Bookmark, Trash2, Users, Link as LinkIcon, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PracticeSheetsPage = () => {
  const { getAuthHeader } = useAuth();
  const [catalog, setCatalog] = useState({ topics: [], grade_bands: [] });
  const [gradeBand, setGradeBand] = useState('grades_3_5');
  const [topics, setTopics] = useState(['addition']);
  const [numProblems, setNumProblems] = useState([20]);
  const [difficulty, setDifficulty] = useState('medium');
  const [includeAnswerKey, setIncludeAnswerKey] = useState(true);
  const [title, setTitle] = useState('');
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'docx' | null

  // After generation, we store the JSON response here to enable downloads
  const [sheet, setSheet] = useState(null); // { title, problems, include_answer_key }

  // Saved templates
  const [templates, setTemplates] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Assign to students
  const [students, setStudents] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignStudentIds, setAssignStudentIds] = useState([]);
  const [assignGenerateLink, setAssignGenerateLink] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const res = await axios.get(`${API}/math/catalog`, { headers: getAuthHeader() });
        setCatalog(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    const loadTemplates = async () => {
      try {
        const res = await axios.get(`${API}/math/practice-sheet/templates`, { headers: getAuthHeader() });
        setTemplates(res.data || []);
      } catch (e) {
        console.error('load templates error', e);
      }
    };
    loadCatalog();
    loadTemplates();
  }, [getAuthHeader]);

  // Load students lazily when assign dialog opens
  const loadStudentsIfNeeded = async () => {
    if (students.length > 0) return;
    try {
      const res = await axios.get(`${API}/students`, { headers: getAuthHeader() });
      setStudents(res.data || []);
    } catch (e) {
      console.error('load students error', e);
      toast.error('Failed to load students');
    }
  };

  const toggleTopic = (id) => {
    setTopics((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  };

  const handleGenerate = async () => {
    if (topics.length === 0) {
      toast.error('Pick at least one topic');
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/math/practice-sheet/generate`,
        {
          grade_band: gradeBand,
          topics,
          num_problems: numProblems[0],
          difficulty,
          include_answer_key: includeAnswerKey,
          title: title.trim() || null,
        },
        {
          headers: { ...getAuthHeader() },
          timeout: 120000,
        }
      );
      // Store JSON response — no auto-open, show success panel instead
      setSheet(res.data);
      toast.success('Practice sheet ready — choose a format below.');
    } catch (err) {
      console.error('practice sheet error', err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error('Generation took too long. Try fewer problems (10-15) or easier difficulty.');
      } else if (err.response?.status === 502 || err.response?.status === 504) {
        toast.error('Server busy — try again in a moment, or reduce the number of problems.');
      } else {
        toast.error('Failed to generate practice sheet');
      }
    } finally {
      setGenerating(false);
    }
  };

  const downloadFile = async (format) => {
    if (!sheet) return;
    setDownloading(format);
    try {
      const res = await axios.post(
        `${API}/math/practice-sheet/${format}`,
        {
          title: sheet.title,
          problems: sheet.problems,
          include_answer_key: sheet.include_answer_key,
        },
        {
          headers: { ...getAuthHeader() },
          responseType: 'blob',
          timeout: 60000,
        }
      );
      const mime =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const blob = new Blob([res.data], { type: mime });
      const safeName = (sheet.title || 'practice_sheet').replace(/[^A-Za-z0-9]+/g, '_').slice(0, 60) || 'practice_sheet';
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${safeName}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 4000);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) {
      console.error(`${format} download error`, err);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  const handlePrint = async () => {
    if (!sheet) return;
    setDownloading('print');
    try {
      const res = await axios.post(
        `${API}/math/practice-sheet/pdf`,
        {
          title: sheet.title,
          problems: sheet.problems,
          include_answer_key: sheet.include_answer_key,
        },
        {
          headers: { ...getAuthHeader() },
          responseType: 'blob',
          timeout: 60000,
        }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (w) {
        // Give the PDF a moment to render, then invoke print
        setTimeout(() => {
          try { w.focus(); w.print(); } catch (_) { /* browser may block */ }
        }, 800);
      } else {
        toast.error('Popup blocked — allow popups to print.');
      }
    } catch (err) {
      console.error('print error', err);
      toast.error('Failed to open print view');
    } finally {
      setDownloading(null);
    }
  };

  const handleGenerateAnother = () => {
    setSheet(null);
  };

  const handleSaveTemplate = async () => {
    const name = templateName.trim();
    if (!name) {
      toast.error('Please enter a template name');
      return;
    }
    if (topics.length === 0) {
      toast.error('Pick at least one topic before saving');
      return;
    }
    setSavingTemplate(true);
    try {
      const res = await axios.post(
        `${API}/math/practice-sheet/templates`,
        {
          name,
          grade_band: gradeBand,
          topics,
          num_problems: numProblems[0],
          difficulty,
          include_answer_key: includeAnswerKey,
          title: title.trim() || null,
        },
        { headers: { ...getAuthHeader() } }
      );
      setTemplates((prev) => [res.data, ...prev]);
      setSaveOpen(false);
      setTemplateName('');
      toast.success('Template saved');
    } catch (err) {
      console.error('save template error', err);
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const applyTemplate = (tpl) => {
    setGradeBand(tpl.grade_band);
    setTopics(tpl.topics || []);
    setNumProblems([tpl.num_problems || 20]);
    setDifficulty(tpl.difficulty || 'medium');
    setIncludeAnswerKey(tpl.include_answer_key !== false);
    setTitle(tpl.title || '');
    toast.success(`Loaded "${tpl.name}"`);
  };

  const deleteTemplate = async (tpl) => {
    try {
      await axios.delete(`${API}/math/practice-sheet/templates/${tpl.id}`, { headers: getAuthHeader() });
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      toast.success('Template deleted');
    } catch (err) {
      console.error('delete template error', err);
      toast.error('Failed to delete template');
    }
  };

  const openAssignDialog = async () => {
    setAssignResult(null);
    setAssignStudentIds([]);
    setAssignGenerateLink(true);
    setAssignOpen(true);
    await loadStudentsIfNeeded();
  };

  const toggleAssignStudent = (sid) => {
    setAssignStudentIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  };

  const handleAssign = async () => {
    if (!sheet) return;
    if (assignStudentIds.length === 0 && !assignGenerateLink) {
      toast.error('Pick at least one student or enable the shareable link');
      return;
    }
    setAssigning(true);
    try {
      const res = await axios.post(
        `${API}/math/practice-sheet/save-and-assign`,
        {
          title: sheet.title,
          problems: sheet.problems,
          grade_band: gradeBand,
          student_ids: assignStudentIds,
          generate_link: assignGenerateLink,
        },
        { headers: { ...getAuthHeader() } }
      );
      setAssignResult(res.data);
      toast.success(
        assignStudentIds.length > 0
          ? `Assigned to ${assignStudentIds.length} student(s)`
          : 'Assignment created with shareable link'
      );
    } catch (err) {
      console.error('assign error', err);
      toast.error(err.response?.data?.detail || 'Failed to assign practice sheet');
    } finally {
      setAssigning(false);
    }
  };

  const closeAssignDialog = () => {
    setAssignOpen(false);
    setTimeout(() => setAssignResult(null), 300);
  };

  const gradeLabels = {
    kindergarten: 'Kindergarten',
    grades_1_2: 'Grades 1-2',
    grades_3_5: 'Grades 3-5',
    grades_6_8: 'Grades 6-8',
    high_school: 'High School',
  };

  const renderSaveDialog = () => (
    <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
      <DialogContent data-testid="ps-save-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif">Save as Template</DialogTitle>
          <DialogDescription>
            Save the current settings (grade, topics, difficulty, count) as a reusable preset.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="tpl-name">Template name</Label>
          <Input
            id="tpl-name"
            placeholder="e.g. Weekly Grade 3 Fractions"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            maxLength={80}
            data-testid="ps-template-name-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setSaveOpen(false)} disabled={savingTemplate} data-testid="ps-save-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSaveTemplate}
            disabled={savingTemplate || !templateName.trim()}
            data-testid="ps-save-confirm-btn"
          >
            {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderAssignDialog = () => (
    <Dialog open={assignOpen} onOpenChange={(o) => (o ? setAssignOpen(true) : closeAssignDialog())}>
      <DialogContent className="max-w-md" data-testid="ps-assign-dialog">
        {assignResult ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                Practice Sheet Assigned
              </DialogTitle>
              <DialogDescription>
                {assignResult.student_count > 0
                  ? `Sent to ${assignResult.student_count} student(s) — they'll take it online with auto-grading.`
                  : 'A shareable link was created — anyone with the link can take it.'}
              </DialogDescription>
            </DialogHeader>
            {assignResult.shareable_link && (
              <div className="space-y-2 py-2">
                <Label className="text-xs text-muted-foreground">Shareable Link</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}${assignResult.shareable_link}`}
                    className="text-xs"
                    data-testid="ps-assign-link-input"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}${assignResult.shareable_link}`);
                      toast.success('Link copied');
                    }}
                    data-testid="ps-assign-copy-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={closeAssignDialog} data-testid="ps-assign-close-btn">Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif">Assign to Students</DialogTitle>
              <DialogDescription>
                Students will take the practice sheet online and receive an auto-graded score.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Students ({assignStudentIds.length} selected)</Label>
                {students.length === 0 ? (
                  <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-3">
                    No students yet. Add students in the Students tab first, or use the shareable link below.
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto space-y-1 border border-border rounded-md p-2">
                    {students.map((s) => {
                      const checked = assignStudentIds.includes(s.id);
                      const label = [s.first_name, s.last_name].filter(Boolean).join(' ') + (s.grade_level ? ` • ${s.grade_level}` : '');
                      return (
                        <label
                          key={s.id}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                            checked ? 'bg-primary/10' : 'hover:bg-secondary/40'
                          }`}
                          data-testid={`ps-assign-student-${s.id}`}
                        >
                          <Checkbox checked={checked} onCheckedChange={() => toggleAssignStudent(s.id)} />
                          <span className="text-sm">{label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/30">
                <div className="flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-primary" />
                  <div>
                    <Label className="text-sm cursor-pointer" htmlFor="ps-assign-link-toggle">Also create shareable link</Label>
                    <p className="text-[11px] text-muted-foreground">Share with anyone (bypasses student login)</p>
                  </div>
                </div>
                <Checkbox
                  id="ps-assign-link-toggle"
                  checked={assignGenerateLink}
                  onCheckedChange={setAssignGenerateLink}
                  data-testid="ps-assign-link-toggle"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeAssignDialog} disabled={assigning} data-testid="ps-assign-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleAssign}
                disabled={assigning || (assignStudentIds.length === 0 && !assignGenerateLink)}
                data-testid="ps-assign-confirm-btn"
              >
                {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Assign
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  // ================ SUCCESS PANEL ================
  if (sheet) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-border" data-testid="ps-success-panel">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="font-serif text-2xl" data-testid="ps-success-title">
                  Practice Sheet Ready
                </CardTitle>
                <CardDescription data-testid="ps-success-summary">
                  <span className="font-medium">{sheet.title}</span>
                  <br />
                  {sheet.problems.length} problems{sheet.include_answer_key ? ' • answer key included' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-2">
                <Button
                  className="w-full h-12 bg-accent hover:bg-accent/90 gap-2"
                  onClick={() => downloadFile('pdf')}
                  disabled={downloading !== null}
                  data-testid="ps-download-pdf-btn"
                >
                  {downloading === 'pdf' ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />Preparing PDF…</>
                  ) : (
                    <><Download className="w-5 h-5" />Download PDF</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-12 gap-2"
                  onClick={() => downloadFile('docx')}
                  disabled={downloading !== null}
                  data-testid="ps-download-docx-btn"
                >
                  {downloading === 'docx' ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />Preparing DOCX…</>
                  ) : (
                    <><FileText className="w-5 h-5" />Download DOCX</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-12 gap-2"
                  onClick={handlePrint}
                  disabled={downloading !== null}
                  data-testid="ps-print-btn"
                >
                  {downloading === 'print' ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />Opening…</>
                  ) : (
                    <><Printer className="w-5 h-5" />Print</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-12 gap-2"
                  onClick={openAssignDialog}
                  disabled={downloading !== null}
                  data-testid="ps-assign-btn"
                >
                  <Users className="w-5 h-5" />
                  Assign to Students
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-12 gap-2"
                  onClick={() => setSaveOpen(true)}
                  disabled={downloading !== null}
                  data-testid="ps-save-template-btn"
                >
                  <BookmarkPlus className="w-5 h-5" />
                  Save as Template
                </Button>
                <Button
                  variant="ghost"
                  className="w-full h-12 gap-2"
                  onClick={handleGenerateAnother}
                  disabled={downloading !== null}
                  data-testid="ps-generate-another-btn"
                >
                  <RefreshCw className="w-5 h-5" />
                  Generate Another Sheet
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </main>
        {renderSaveDialog()}
        {renderAssignDialog()}
      </div>
    );
  }

  // ================ CONFIG PANEL ================
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-6">
            <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-1">
              Math Practice Sheets
            </h1>
            <p className="text-muted-foreground">
              Print a fresh worksheet in seconds — pick a topic, hit generate, choose PDF or DOCX.
            </p>
          </div>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                One-Click Worksheet
              </CardTitle>
              <CardDescription>
                Great for daily homeschool practice. Fresh problems every time. Answer key included on the last page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {templates.length > 0 && (
                <div className="space-y-2" data-testid="ps-templates-section">
                  <div className="flex items-center gap-2">
                    <Bookmark className="w-4 h-4 text-primary" />
                    <Label>My Templates</Label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {templates.map((tpl) => (
                      <div
                        key={tpl.id}
                        className="group flex items-center gap-1 rounded-full border border-border bg-secondary/40 pl-3 pr-1 py-1 text-sm hover:border-primary/60 transition-colors"
                        data-testid={`ps-template-chip-${tpl.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => applyTemplate(tpl)}
                          className="max-w-[220px] truncate"
                          data-testid={`ps-template-apply-${tpl.id}`}
                        >
                          {tpl.name}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteTemplate(tpl)}
                          className="opacity-40 hover:opacity-100 hover:text-destructive p-1 rounded-full transition"
                          aria-label={`Delete template ${tpl.name}`}
                          data-testid={`ps-template-delete-${tpl.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Grade Level</Label>
                  <Select value={gradeBand} onValueChange={setGradeBand}>
                    <SelectTrigger className="h-11" data-testid="ps-grade-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {catalog.grade_bands.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {gradeLabels[g.id] || g.label.split(' (')[0]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Difficulty</Label>
                  <div className="flex gap-2">
                    {['easy', 'medium', 'hard'].map((d) => (
                      <Button
                        key={d}
                        variant={difficulty === d ? 'default' : 'outline'}
                        className="flex-1 capitalize"
                        onClick={() => setDifficulty(d)}
                        data-testid={`ps-difficulty-${d}`}
                      >
                        {d}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Topics ({topics.length} selected)</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {catalog.topics.map((t) => (
                    <div
                      key={t.id}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        topics.includes(t.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40'
                      }`}
                      onClick={() => toggleTopic(t.id)}
                      data-testid={`ps-topic-${t.id}`}
                    >
                      <Checkbox checked={topics.includes(t.id)} onCheckedChange={() => toggleTopic(t.id)} />
                      <span className="text-sm font-medium">{t.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Number of Problems</Label>
                  <span className="text-2xl font-serif font-medium text-primary" data-testid="ps-num-count">
                    {numProblems[0]}
                  </span>
                </div>
                <Slider
                  value={numProblems}
                  onValueChange={setNumProblems}
                  min={5}
                  max={40}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>5</span>
                  <span>40</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Optional custom title</Label>
                <Input
                  placeholder="Leave blank for auto-generated title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="h-11"
                  data-testid="ps-title-input"
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-secondary/30">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium cursor-pointer" htmlFor="ps-key">
                    Include Answer Key
                  </Label>
                  <p className="text-xs text-muted-foreground">Adds a final page with all answers</p>
                </div>
                <Checkbox
                  id="ps-key"
                  checked={includeAnswerKey}
                  onCheckedChange={setIncludeAnswerKey}
                  data-testid="ps-answer-key-toggle"
                />
              </div>

              <Button
                className="w-full h-14 text-base bg-accent hover:bg-accent/90 gap-2"
                onClick={handleGenerate}
                disabled={generating || topics.length === 0}
                data-testid="ps-generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating... (may take up to 90 seconds)
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Practice Sheet
                  </>
                )}
              </Button>

              <div className="flex gap-3 text-xs text-muted-foreground bg-secondary/40 p-4 rounded-lg">
                <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  Practice sheets are throwaway — nothing is saved to your library or assigned to students.
                  Every generation produces fresh problems. Choose PDF or DOCX after generation.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
      {renderSaveDialog()}
      {renderAssignDialog()}
    </div>
  );
};

export default PracticeSheetsPage;
